import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/api';
import { getSupportedAudioRecorderFormat } from '../utils/mediaRecorderUtils';
import './VoiceReRecord.css';

const MIN_RECORD_SECONDS = 30;
// Check status periodically so UI updates when backend responds (completed/failed)
const STATUS_CHECK_INTERVAL_MS = 3000;

/**
 * @param {Object} props
 * @param {boolean} [props.externalRecordingInProgress] - When true, another section (e.g. AI personalization) is recording; disables "Start cloning"
 * @param { (recording: boolean) => void } [props.onRecordingChange] - Called when inline recording here starts (true) or stops (false)
 */
function VoiceCloning({ externalRecordingInProgress = false, onRecordingChange } = {}) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [voiceName, setVoiceName] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);
  const cardRef = useRef(null);

  // Inline recording (when user clicks "Record a longer sample")
  const [showInlineRecorder, setShowInlineRecorder] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordBlob, setRecordBlob] = useState(null);
  const [recordUrl, setRecordUrl] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [recordError, setRecordError] = useState(null);
  const [recordSuccess, setRecordSuccess] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [actionSuccess, setActionSuccess] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [lastCheckMessage, setLastCheckMessage] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);

  // Map backend English error strings to i18n keys
  const translateBackendError = useCallback((msg) => {
    if (!msg || typeof msg !== 'string') return msg;
    if (msg.includes('Empty upload')) return t('voiceCloning.errorEmptyUpload');
    if (msg.includes('too large')) {
      const match = msg.match(/(\d+)\s*MB/);
      return t('voiceCloning.errorFileTooLarge', { max: match ? match[1] : '10' });
    }
    if (msg.includes('Invalid audio file')) return t('voiceCloning.errorInvalidAudioFile');
    if (msg.includes('Invalid content type')) return t('voiceCloning.errorInvalidContentType');
    if (msg.includes('must be at least') && msg.includes('seconds')) {
      const reqMatch = msg.match(/at least (\d+) seconds/);
      const curMatch = msg.match(/current:\s*(\d+\.?\d*)/);
      return t('voiceCloning.errorTooShortBackend', { required: reqMatch?.[1] ?? '30', current: curMatch?.[1] ?? '?' });
    }
    if (msg.includes('record your voice first')) return t('voiceCloning.errorNoVoiceFile');
    if (msg.includes('Voice file not found')) return t('voiceCloning.errorVoiceFileNotFound');
    if (msg.includes('too short')) {
      const m = msg.match(/(\d+) seconds required/);
      return t('voiceCloning.errorVoiceTooShortClone', { required: m?.[1] ?? '30' });
    }
    if (msg.includes('task queue')) return t('voiceCloning.errorTaskQueueUnavailable');
    if (msg.includes('Nothing to reset')) return t('voiceCloning.errorNothingToReset');
    if (msg.includes('No completed voice clone')) return t('voiceCloning.errorNoCompletedClone');
    return msg;
    // ``t`` is intentionally NOT a dep — see fetchStatus below for the
    // rationale. i18next resolves keys at call time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStatus = useCallback(async () => {
    setActionError(null);
    try {
      const res = await apiClient.get('/api/v1/voice-clone/status');
      setData(res.data);
      return res.data;
    } catch (e) {
      const msg = e.response?.data?.detail ?? e.message ?? t('voiceCloning.errorUploadFailed');
      const raw = typeof msg === 'string' ? msg : JSON.stringify(msg);
      setActionError(translateBackendError(raw));
      return null;
    } finally {
      setLoading(false);
    }
    // ``t`` reference flips during i18next async locale load; depending
    // on it would re-create ``fetchStatus`` on every flip, re-firing
    // ``useEffect([fetchStatus])`` and the polling interval below in
    // rapid succession. Translations resolve at call time so a stale
    // closure is harmless here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translateBackendError]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const isWaitingForClone = data && (data.status === 'pending' || data.status === 'processing') && data.has_voice_file;
  useEffect(() => {
    if (!isWaitingForClone) return;
    const interval = setInterval(fetchStatus, STATUS_CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isWaitingForClone, fetchStatus]);

  useEffect(() => {
    if (!data?.sample_url) {
      setPreviewBlobUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      return;
    }
    let cancelled = false;
    apiClient.get(data.sample_url, { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        setPreviewBlobUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(res.data);
        });
      })
      .catch(() => {
        if (!cancelled) setPreviewBlobUrl(null);
      });
    return () => { cancelled = true; };
  }, [data?.sample_url]);

  useEffect(() => {
    return () => {
      setPreviewBlobUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, []);

  useEffect(() => {
    if (recordUrl) return () => URL.revokeObjectURL(recordUrl);
  }, [recordUrl]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      onRecordingChange?.(false);
    };
  }, [onRecordingChange]);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const startInlineRecording = async () => {
    setRecordError(null);
    setRecordSuccess(false);
    try {
      const { mimeType, extension } = getSupportedAudioRecorderFormat();
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      recorder.onstop = () => {
        const blobType = recorder.mimeType || mimeType || 'audio/webm';
        const blob = new Blob(audioChunksRef.current, { type: blobType });
        setRecordBlob(blob);
        setRecordUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach((t) => t.stop());
      };
      recorder.start();
      setIsRecording(true);
      onRecordingChange?.(true);
      setRecordingTime(0);
      timerRef.current = setInterval(() => setRecordingTime((n) => n + 1), 1000);
    } catch (e) {
      setRecordError(e.message || t('voiceCloning.errorMicrophoneRequired'));
    }
  };

  const stopInlineRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      onRecordingChange?.(false);
      if (timerRef.current) clearInterval(timerRef.current);
    }
  };

  const resetInlineRecording = () => {
    setRecordBlob(null);
    if (recordUrl) URL.revokeObjectURL(recordUrl);
    setRecordUrl(null);
    setRecordingTime(0);
    setRecordError(null);
    setRecordSuccess(false);
  };

  const uploadInlineRecording = async () => {
    if (!recordBlob) return;
    if (recordingTime < MIN_RECORD_SECONDS) {
      setRecordError(t('voiceCloning.audioTooShort', { current: recordingTime, required: MIN_RECORD_SECONDS }));
      return;
    }
    setUploading(true);
    setRecordError(null);
    setRecordSuccess(false);
    try {
      const { extension } = getSupportedAudioRecorderFormat();
      const formData = new FormData();
      formData.append('voice_file', recordBlob, `voice.${extension}`);
      await apiClient.post('/api/v1/onboarding/voice/re-record', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setRecordBlob(null);
      if (recordUrl) URL.revokeObjectURL(recordUrl);
      setRecordUrl(null);
      setRecordingTime(0);
      setRecordError(null);
      setRecordSuccess(true);
      setTimeout(() => {
        setShowInlineRecorder(false);
        setRecordSuccess(false);
        fetchStatus();
      }, 1500);
    } catch (e) {
      const d = e.response?.data?.detail;
      const raw = Array.isArray(d) ? d.map((x) => x.msg || x).join(', ') : typeof d === 'string' ? d : t('voiceCloning.errorUploadFailed');
      setRecordError(translateBackendError(raw));
    } finally {
      setUploading(false);
    }
  };

  const handleStart = async () => {
    setActionError(null);
    setStarting(true);
    try {
      await apiClient.post('/api/v1/voice-clone/start', {
        voice_name: voiceName.trim() || undefined,
      });
      // Optimistic update: show "Creating..." immediately so the UI clearly changes
      setData((prev) => (prev ? { ...prev, status: 'pending' } : prev));
      await fetchStatus();
      // Scroll card into view so user sees the processing state
      setTimeout(() => cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const raw = Array.isArray(detail)
        ? detail.map((x) => x.msg || x).join(', ')
        : typeof detail === 'string'
          ? detail
          : t('voiceCloning.errorStarting');
      setActionError(translateBackendError(raw));
    } finally {
      setStarting(false);
    }
  };

  const handleRetry = () => {
    setActionError(null);
    handleStart();
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
    setActionError(null);
  };

  const handleDeleteConfirm = async () => {
    setShowDeleteConfirm(false);
    setActionError(null);
    setActionSuccess(null);
    setDeleting(true);
    try {
      await apiClient.delete('/api/v1/voice-clone/');
      await fetchStatus();
      setActionSuccess(t('voiceCloning.deleteSuccess'));
      setTimeout(() => setActionSuccess(null), 4000);
    } catch (e) {
      const raw = e.response?.data?.detail || t('voiceCloning.errorDeleting');
      setActionError(translateBackendError(typeof raw === 'string' ? raw : JSON.stringify(raw)));
    } finally {
      setDeleting(false);
    }
  };

  const handleRegeneratePreview = async () => {
    setActionError(null);
    setRegenerating(true);
    try {
      await apiClient.post('/api/v1/voice-clone/regenerate-preview');
      await fetchStatus();
    } catch (e) {
      const raw = e.response?.data?.detail || t('voiceCloning.errorRegenerating');
      setActionError(translateBackendError(typeof raw === 'string' ? raw : JSON.stringify(raw)));
    } finally {
      setRegenerating(false);
    }
  };

  const onPlayPause = () => {
    if (!audioRef.current) return;
    if (isPlaying) audioRef.current.pause();
    else audioRef.current.play();
    setIsPlaying(!isPlaying);
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' });
    } catch {
      return iso;
    }
  };

  // e.g. "04-Feb-2026" for "Your voice recording: X seconds (recorded 04-Feb-2026)"
  const formatRecordedDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      const day = d.getDate().toString().padStart(2, '0');
      const month = d.toLocaleDateString('en-GB', { month: 'short' });
      return `${day}-${month}-${d.getFullYear()}`;
    } catch {
      return iso;
    }
  };

  if (loading && !data) {
    return (
      <div className="voice-rerecord-card">
        <div className="voice-rerecord-header">
          <h2>{t('voiceCloning.title')}</h2>
          <p className="hint">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  const status = data?.status;
  const hasVoiceFile = data?.has_voice_file;
  const meetsDuration = data?.meets_duration;
  const voiceDuration = data?.voice_duration ?? 0;
  const minRequired = data?.min_duration_required ?? 30;
  const maxRetries = data?.max_retries ?? 3;

  return (
    <div ref={cardRef} className="voice-rerecord-card">
      <div className="voice-rerecord-header">
        <h2>{t('voiceCloning.title')}</h2>
        <p>{t('voiceCloning.description')}</p>
        {status === 'completed' && data?.cloned_at && (
          <div className="voice-rerecord-meta">
            <span className="pill">{t('voiceCloning.clonedOn', { date: formatDate(data.cloned_at) })}</span>
          </div>
        )}
      </div>

      {actionError && <div className="error">{actionError}</div>}
      {actionSuccess && <div className="success">{actionSuccess}</div>}

      {showDeleteConfirm &&
        createPortal(
          <div className="voice-clone-modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
            <div className="voice-clone-modal" onClick={(e) => e.stopPropagation()}>
              <p>{t('voiceCloning.confirmDelete')}</p>
              <div className="voice-rerecord-buttons">
                <button type="button" className="secondary" onClick={() => setShowDeleteConfirm(false)}>
                  {t('common.cancel')}
                </button>
                <button type="button" className="danger" onClick={handleDeleteConfirm} disabled={deleting}>
                  {deleting ? t('common.loading') : t('voiceCloning.delete')}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {(!hasVoiceFile || !meetsDuration) && !showInlineRecorder && (
        <div className="voice-rerecord-actions">
          <p style={{ margin: '0 0 10px 0', color: '#555' }}>
            {!hasVoiceFile
              ? t('voiceCloning.noVoiceFile')
              : t('voiceCloning.audioTooShort', { current: Math.round(voiceDuration), required: minRequired })}
          </p>
          <button type="button" className="primary" onClick={() => setShowInlineRecorder(true)}>
            {t('voiceCloning.reRecordLink')}
          </button>
        </div>
      )}

      {showInlineRecorder && (
        <div className="voice-rerecord-actions">
          {!recordUrl ? (
            <>
              {!isRecording ? (
                <button
                  type="button"
                  className="primary"
                  onClick={startInlineRecording}
                  disabled={uploading || recordSuccess}
                >
                  {t('voiceCloning.startRecording')}
                </button>
              ) : (
                <div className="recording-row" style={{ marginBottom: 10 }}>
                  <span className="recording-dot" />
                  <span className="recording-time">{formatTime(recordingTime)}</span>
                  <button type="button" className="danger" onClick={stopInlineRecording}>
                    {t('voiceCloning.stopRecording')}
                  </button>
                </div>
              )}
              <p className="hint">{t('voiceCloning.recordHint', { required: minRequired })}</p>
            </>
          ) : (
            <div className="voice-rerecord-preview">
              <audio src={recordUrl} controls />
              <div className="voice-rerecord-buttons">
                <button type="button" className="secondary" onClick={resetInlineRecording} disabled={uploading}>
                  {t('voiceCloning.reRecord')}
                </button>
                <button type="button" className="primary" onClick={uploadInlineRecording} disabled={uploading}>
                  {uploading ? t('common.loading') : t('voiceCloning.upload')}
                </button>
              </div>
              <p className="hint">
                {recordingTime < MIN_RECORD_SECONDS
                  ? t('voiceCloning.audioTooShort', { current: recordingTime, required: MIN_RECORD_SECONDS })
                  : t('voiceCloning.minimumReached', { required: MIN_RECORD_SECONDS })}
              </p>
            </div>
          )}
          {recordSuccess && <div className="success">{t('voiceCloning.voiceSampleUpdated')}</div>}
          {recordError && <div className="error">{recordError}</div>}
          <button
            type="button"
            className="secondary"
            style={{ marginTop: 12 }}
            onClick={() => { setShowInlineRecorder(false); resetInlineRecording(); }}
          >
            {t('voiceCloning.cancelRecording')}
          </button>
        </div>
      )}

      {hasVoiceFile && meetsDuration && !status && !starting && !showInlineRecorder && (
        <>
          <div className="voice-rerecord-actions" style={{ marginBottom: 12 }}>
            <label htmlFor="voice-clone-name" style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 14 }}>
              {t('voiceCloning.voiceNameLabel')}
            </label>
            <input
              id="voice-clone-name"
              type="text"
              value={voiceName}
              onChange={(e) => setVoiceName(e.target.value)}
              placeholder={t('voiceCloning.voiceNamePlaceholder')}
              maxLength={100}
              style={{ width: '100%', maxWidth: 320, padding: '10px 14px', border: '2px solid #e5e7eb', borderRadius: 10, marginBottom: 10 }}
            />
            <button
              type="button"
              className="primary"
              onClick={handleStart}
              disabled={starting || isRecording || externalRecordingInProgress}
              title={isRecording || externalRecordingInProgress ? t('voiceCloning.disabledWhileRecording') : undefined}
            >
              {t('voiceCloning.startCloning')}
            </button>
          </div>
          <p className="hint">
            {t('voiceCloning.existingVoice', {
              duration: Math.round(voiceDuration),
              date: formatRecordedDate(data?.voice_recorded_at),
            })}
          </p>
          <button
            type="button"
            className="secondary"
            style={{ marginTop: 8 }}
            onClick={() => setShowInlineRecorder(true)}
          >
            {t('voiceCloning.reRecordLink')}
          </button>
        </>
      )}

      {(status === 'pending' || status === 'processing' || starting) && hasVoiceFile && meetsDuration && (
        <div className="voice-rerecord-actions">
          <div className="recording-row" style={{ marginBottom: 6 }}>
            <span className="recording-dot" style={{ background: '#2563eb', boxShadow: '0 0 0 6px rgba(37, 99, 235, 0.2)' }} />
            <span className="recording-time">
              {starting ? t('voiceCloning.starting') : t('voiceCloning.processingMessage')}
            </span>
          </div>
          <div className="voice-clone-progress-wrap" role="progressbar" aria-valuetext={t('voiceCloning.processingMessage')} aria-label={t('voiceCloning.processingMessage')}>
            <div className="voice-clone-progress-bar" />
          </div>
          <p className="hint">{t('voiceCloning.processingNote')}</p>
          <p className="hint">{t('voiceCloning.updatesWhenReady')}</p>
          {lastCheckMessage && (
            <p className="hint" style={{ marginTop: 8, fontWeight: 600, color: '#2563eb' }}>
              {lastCheckMessage}
            </p>
          )}
          <div className="voice-rerecord-buttons" style={{ marginTop: 8 }}>
            <button
              type="button"
              className="secondary"
              disabled={checkingStatus}
              onClick={async () => {
                setCheckingStatus(true);
                setLastCheckMessage(null);
                try {
                  const result = await fetchStatus();
                  if (result) {
                    if (result.status === 'completed') {
                      setLastCheckMessage(t('voiceCloning.statusReady'));
                    } else if (result.status === 'failed') {
                      setLastCheckMessage(t('voiceCloning.statusFailed'));
                    } else {
                      setLastCheckMessage(t('voiceCloning.statusStillCreating'));
                    }
                  } else {
                    setLastCheckMessage(t('voiceCloning.statusCheckError'));
                  }
                  setTimeout(() => setLastCheckMessage(null), 5000);
                } finally {
                  setCheckingStatus(false);
                }
              }}
            >
              {checkingStatus ? t('voiceCloning.checking') : t('voiceCloning.checkStatus')}
            </button>
            <button
              type="button"
              className="secondary"
              disabled={checkingStatus}
              onClick={async () => {
                setActionError(null);
                setCheckingStatus(true);
                try {
                  await apiClient.post('/api/v1/voice-clone/reset');
                  await fetchStatus();
                } catch (e) {
                  const msg = e.response?.data?.detail ?? e.message ?? t('voiceCloning.errorResetFailed');
                  setActionError(translateBackendError(typeof msg === 'string' ? msg : JSON.stringify(msg)));
                } finally {
                  setCheckingStatus(false);
                }
              }}
            >
              {t('voiceCloning.startOver')}
            </button>
          </div>
        </div>
      )}

      {status === 'completed' && (
        <div className="voice-rerecord-preview">
          <p style={{ margin: '0 0 8px 0', fontWeight: 600 }}>{t('voiceCloning.previewTitle')}</p>
          {previewBlobUrl && (
            <>
              <audio
                ref={audioRef}
                src={previewBlobUrl}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onEnded={() => setIsPlaying(false)}
              />
              <div className="voice-rerecord-buttons">
                <button type="button" className="secondary" onClick={onPlayPause}>
                  {isPlaying ? t('voiceCloning.pause') : t('voiceCloning.play')}
                </button>
              </div>
            </>
          )}
          <p className="hint" style={{ marginTop: 8 }}>{t('voiceCloning.usageNote')}</p>
          <div className="voice-rerecord-buttons" style={{ marginTop: 12 }}>
            <button
              type="button"
              className="secondary"
              onClick={handleRegeneratePreview}
              disabled={regenerating}
            >
              {regenerating ? t('common.loading') : t('voiceCloning.regenerate')}
            </button>
            <button
              type="button"
              className="danger"
              onClick={handleDeleteClick}
              disabled={deleting}
            >
              {t('voiceCloning.delete')}
            </button>
          </div>
        </div>
      )}

      {status === 'failed' && (
        <div className="voice-rerecord-actions">
          <p className="error">{t('voiceCloning.failedMessage')}</p>
          {data?.error && <p className="hint">{data.error}</p>}
          <p className="hint">{t('voiceCloning.retriesExhausted', { max: maxRetries })}</p>
          <button type="button" className="primary" onClick={handleRetry}>
            {t('voiceCloning.retry')}
          </button>
        </div>
      )}
    </div>
  );
}

export default VoiceCloning;
